import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useParams } from 'react-router-dom';
import { PublicClientApplication, EventType } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { useTranslation } from 'react-i18next';
import { msalConfig } from './config/authConfig';
import { AuthProvider } from './contexts/AuthContext';
import { Layout } from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import Home from './pages/Home/Home';
import ClientCredentials from './pages/ClientCredentials';
import SignIn from './pages/SignIn/SignIn';
import { ROUTES } from './constants/routes';

const msalInstance = new PublicClientApplication(msalConfig);

// Component to handle language from URL
const LanguageHandler: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { lang } = useParams<{ lang?: string }>();
    const { i18n } = useTranslation();

    useEffect(() => {
        const validLangs = ['en', 'fr'];
        
        if (lang && validLangs.includes(lang)) {
            // URL has a valid language suffix — sync i18n
            if (i18n.language !== lang) {
                i18n.changeLanguage(lang);
            }
        } else if (!lang) {
            // No language suffix in URL means English
            if (i18n.language !== 'en') {
                i18n.changeLanguage('en');
            }
        }
    }, [lang, i18n]);

    return <>{children}</>;
};

const AppContent: React.FC = () => {
    const { t } = useTranslation();
    const [isInitialized, setIsInitialized] = useState(false);

    useEffect(() => {
        const initializeMsal = async () => {
            try {
                await msalInstance.initialize();
                msalInstance.addEventCallback((event) => {
                    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
                        // Login success handled by auth context and MSAL state.
                    }
                });
                
                setIsInitialized(true);
            } catch (error) {
                console.error('Failed to initialize MSAL:', error);
                setIsInitialized(true);
            }
        };

        initializeMsal();
    }, []);

    if (!isInitialized) {
        return <div>{t('auth.authCallback')}</div>;
    }

    return (
        <MsalProvider instance={msalInstance}>
            <AuthProvider>
                <Router>
                    <Layout>
                        <Routes>
                            {/* Routes with language suffix — must be BEFORE base routes to match first */}
                            <Route path="/client-credentials/:lang" 
                                element={
                                    <LanguageHandler>
                                        <ProtectedRoute>
                                            <ClientCredentials />
                                        </ProtectedRoute>
                                    </LanguageHandler>
                                } 
                            />
                            <Route path="/signin/:lang" element={
                                <LanguageHandler>
                                    <SignIn />
                                </LanguageHandler>
                            } />
                            <Route path="/:lang" element={
                                <LanguageHandler>
                                    <Home />
                                </LanguageHandler>
                            } />

                            {/* Base routes (English — no suffix) */}
                            <Route path={ROUTES.HOME} element={
                                <LanguageHandler>
                                    <Home />
                                </LanguageHandler>
                            } />
                            <Route path={ROUTES.SIGNIN} element={
                                <SignIn />
                            } />
                            <Route 
                                path={ROUTES.CLIENT_CREDENTIALS} 
                                element={
                                    <LanguageHandler>
                                        <ProtectedRoute>
                                            <ClientCredentials />
                                        </ProtectedRoute>
                                    </LanguageHandler>
                                } 
                            />
                        </Routes>
                    </Layout>
                </Router>
            </AuthProvider>
        </MsalProvider>
    );
};

const App: React.FC = () => {
    return <AppContent />;
};

export default App;